import localforage from 'localforage';
import {
  uploadToGoogleDrive,
  findOrCreateGoogleDriveFolder
} from '../utils/googleDrive';
import {
  COMPANY_KEY,
  CLIENTS_KEY,
  PRODUCTS_KEY,
  INVOICES_KEY,
  INVOICE_DETAILS,
  CLIENT_FORM_KEY,
  PRODUCT_FORM_KEY,
  INVOICE_FORM_KEY,
  DEFAULT_INVOICE_COLOR,
  DEFAULT_INVOICE_BG,
  QUOTATIONS_KEY,
  QUOTATION_DETAILS,
  QUOTATION_FORM_KEY,
  DEFAULT_QUOTATION_COLOR,
  DEFAULT_QUOTATION_BG,
} from '../constants/localKeys';
import secureLogger from '../utils/secureLogger';
import { mergeRowsById } from '../utils/syncMeta';
import { isShapeCheckableKey, validateCollection } from '../utils/schemaGuard';
import {
  ASSETS_KEY,
  isAssetKey,
  mergeIncomingAssets,
  invalidateAssetsCache,
  setItem as compactingSetItem,
} from './assetStorage';
import {
  clearGoogleAuth,
  getStoredGoogleAuth,
  persistGoogleAuth,
  isStoredTokenExpiringSoon,
  requestTokenRefresh,
} from './googleTokenService';

// Cloud sync configuration
const SYNC_CONFIG = {
  APP_FOLDER_NAME: 'Logislip_Data',
  BACKUP_FILE_NAME: 'logislip_backup.json',
  BACKUP_PREVIOUS_FILE_NAME: 'logislip_backup.previous.json',
  METADATA_FILE_NAME: 'sync_metadata.json',
  AUTO_SYNC_INTERVAL: 5 * 60 * 1000, // 5 minutes
  MAX_RETRIES: 3,
  RETRY_DELAY: 2000,
  VERSION_HISTORY_LIMIT: 10,
  BACKUP_ROTATION_INTERVAL_MS: 24 * 60 * 60 * 1000, // Rotate at most once per day
  // Cold-storage / archive settings
  ARCHIVE_FOLDER_NAME: 'cold_storage',
  ARCHIVE_FILE_NAME: 'archived_invoices.json',
  ARCHIVE_THRESHOLD_DAYS: 90, // Paid invoices older than this are archived
  // Versioned-snapshot settings (AWS RDS–style retention)
  //   - Automated snapshots are server-copied daily into VERSIONS_FOLDER_NAME
  //     with a timestamped name. The oldest are pruned beyond retention.
  //   - Manual snapshots are never pruned (they carry a distinct prefix).
  VERSIONS_FOLDER_NAME: 'versions',
  VERSION_AUTO_PREFIX: 'logislip_backup_auto_',
  VERSION_MANUAL_PREFIX: 'logislip_backup_manual_',
  VERSION_AUTO_RETENTION: 2,   // keep last 2 automated snapshots (user pref)
};

// Dedicated localforage key used to persist sync coordination state
// (dirtyKeys + syncQueue) across reloads. Excluded from SYNC_KEYS.
const SYNC_STATE_KEY = '__cloudSync_state';

// Hash placeholder used while computing the embedded _metadata.hash so
// the field exists during hashing and verifies cleanly on download.
const HASH_PLACEHOLDER = '__pending__';

// Hash scheme version stamped into _metadata.hashVersion.
//   v1 — legacy. Used `JSON.stringify(data, Object.keys(data).sort())`, where
//        the sorted-keys array is a JSON.stringify REPLACER, not a sorter.
//        That allow-list filter is applied at every nesting level, so nested
//        keys not also present at the top level silently drop out of the
//        hash input. Collisions galore + any top-level-key drift (e.g.
//        _archivedInvoiceIds added in one build, not another) invalidates the
//        hash on download. The false-mismatch that locked users out of
//        empty-local disaster recovery was caused by exactly this.
//   v2 — this file. Proper recursive key-sorted serialization with no key
//        filtering, undefined-dropping on objects (matching JSON.stringify),
//        and Date→ISO normalization so hashes stay stable across IDB (Date
//        instances) vs Drive blob (ISO strings after JSON round-trip).
// Legacy v1 blobs pass verifyDataHash without re-hashing so first sync after
// upgrade doesn't lock users out; the next upload stamps v2.
const HASH_VERSION = 2;

// Schema version stamped into every uploaded backup. Bump only when the
// on-disk shape changes in a way that needs migration on read.
// Newer-than-current blobs are refused to avoid an old client overwriting
// fresh data with a degraded copy. Older blobs are run through
// migrateCloudData() before applying.
const CURRENT_SCHEMA_VERSION = 1;

// localStorage key used to detect when a different Google account signs in
// on the same device. On switch we clear the local sync caches to prevent
// one user's data from being uploaded under another user's Drive folder.
const LAST_USER_KEY = 'cloudSync_lastUserId';

// Data keys that need to be synced
const SYNC_KEYS = [
  COMPANY_KEY,
  CLIENTS_KEY,
  PRODUCTS_KEY,
  INVOICES_KEY,
  INVOICE_DETAILS,
  CLIENT_FORM_KEY,
  PRODUCT_FORM_KEY,
  INVOICE_FORM_KEY,
  DEFAULT_INVOICE_COLOR,
  DEFAULT_INVOICE_BG,
  QUOTATIONS_KEY,
  QUOTATION_DETAILS,
  QUOTATION_FORM_KEY,
  DEFAULT_QUOTATION_COLOR,
  DEFAULT_QUOTATION_BG,
];

// Max entries kept in the recentFailures ring buffer (oldest dropped first).
const RECENT_FAILURES_CAP = 20;

// Keys whose value is an array of rows with stable `id`. These get per-row
// merge by updatedAt on cloud apply (instead of full-array replace) so that
// concurrent edits on different devices don't clobber each other. Rows
// carry tombstones (_deleted: true) when soft-deleted — see utils/syncMeta.
const MERGEABLE_ARRAY_KEYS = new Set([
  CLIENTS_KEY,
  PRODUCTS_KEY,
  INVOICES_KEY,
  INVOICE_DETAILS,
  QUOTATIONS_KEY,
  QUOTATION_DETAILS,
]);

class CloudSyncService {
  constructor() {
    this.accessToken = null;
    this.userId = null;
    this.appFolderId = null;
    this.isOnline = navigator.onLine;
    this.syncQueue = [];
    this.lastSyncTime = null;
    this.syncInProgress = false;
    this.autoSyncInterval = null;
    this.eventListeners = new Set();
    this.dataSyncTimeout = null;

    // Incremental sync tracking
    this.dirtyKeys = new Set();        // Keys modified since last successful sync
    this.backupFileId = null;          // Cached Google Drive file ID (skip search on every sync)
    this.previousBackupFileId = null;  // Cached previous-generation backup file ID
    // Re-entrancy counter (replaces a boolean): incremented around every cloud
    // apply, decremented in finally. Hooks ignore writes while > 0. A counter
    // is needed because two applies can interleave (e.g. initial restore +
    // auto-sync arriving together) and a boolean would flip false too early.
    this.cloudApplyDepth = 0;

    // Cold-storage / archive state
    this.archiveFolderId = null;       // Cached cold_storage/ folder ID
    this.archiveFileId = null;         // Cached archived_invoices.json file ID

    // Versioned-snapshot state
    this.versionsFolderId = null;      // Cached versions/ folder ID

    // Failure-mode telemetry: small ring buffer of recent sync_failed events
    // so the UI (and future diagnostics) can surface what's been going wrong
    // without having to replay the logs. Keyed by stage so a sustained
    // hash_mismatch or schema_too_new is visible at a glance.
    this.recentFailures = [];          // Last RECENT_FAILURES_CAP entries
    this.failureCountsByStage = {};    // stage -> running count for session

    // Initialize sync metadata in localStorage
    this.initializeSyncMetadata();

    // Restore in-flight sync state (dirtyKeys + queue) from localforage
    this.restoreSyncState();

    // Try to restore previous authentication state
    this.restoreAuthState();

    // Setup online/offline detection
    this.setupNetworkListeners();

    // Setup periodic sync
    this.setupAutoSync();

    // CloudSyncService initialized. Dirty-tracking is driven by explicit
    // calls from syncedStorage → triggerSync(); the old localforage monkey-
    // patch was removed in Tier 3c.
  }

  // Restore authentication state from localStorage
  async restoreAuthState() {
    try {
      const authState = getStoredGoogleAuth();
      if (authState) {
        const { accessToken, userId } = authState;
        
        if (accessToken && userId) {
          // Check if we have network connectivity before attempting to initialize
          if (!navigator.onLine) {
            secureLogger.debug('Device is offline, skipping cloud sync initialization');
            return false;
          }
          
          await this.initialize(accessToken, userId);
          return true;
        }
      }
    } catch (error) {
      secureLogger.debug('Cloud sync not available or not configured:', { hasAuth: !!getStoredGoogleAuth() });
      // Clear invalid auth state to prevent repeated failures
      clearGoogleAuth();
    }
    return false;
  }

  // Store authentication state in localStorage
  async storeAuthState(accessToken, userId) {
    try {
      persistGoogleAuth(accessToken, userId);
    } catch (error) {
      secureLogger.error('Failed to persist authentication state:', error);
    }
  }

  // Initialize the service
  async initialize(accessToken, userId) {
    try {
      // Validate access token format
      if (!accessToken || !accessToken.startsWith('ya29.')) {
        secureLogger.debug('Access token does not appear to be a valid Google token');
        return false;
      }
      
      // Check network connectivity
      if (!navigator.onLine) {
        secureLogger.debug('Device is offline, cannot initialize cloud sync');
        return false;
      }
      
      this.accessToken = accessToken;
      this.userId = userId;

      // If a different Google account signed in on this device, wipe the
      // local sync caches before touching Drive. Prevents the previous
      // user's IndexedDB data from being uploaded into the new user's
      // backup file (or vice-versa, on first sync after switch).
      await this._handleUserSwitch(userId);

      // Create or find app folder
      await this.setupAppFolder();

      // Persist authentication state
      await this.storeAuthState(accessToken, userId);

      // Pull drive→local before returning so the caller (sign-in flow) unblocks
      // over a cloud-fresh state and no user write happens on stale data. Still
      // non-fatal on failure: the user can work offline or force-sync later.
      try {
        await this.performSync('initialize');
      } catch (error) {
        secureLogger.debug('Initial sync failed, continuing without cloud sync:', { message: error.message });
      }

      // Notify listeners
      this.notifyListeners('initialized', { success: true });

      return true;
    } catch (error) {
      secureLogger.debug('Failed to initialize Cloud Sync Service (continuing without cloud sync):', { 
        message: error.message,
        hasToken: !!accessToken,
        isOnline: navigator.onLine 
      });
      this.clearAuthenticationState();
      this.notifyListeners('initialized', { success: false, error });
      return false;
    }
  }

  // Setup app folder in Google Drive
  async setupAppFolder() {
    try {
      // const metadata = this.getSyncMetadata();
      // const deviceId = metadata.deviceId || this.generateDeviceId();
      
      // Use simpler folder name without device ID to avoid duplicates
      // Each user will have one main folder, and we'll use file versioning for conflict resolution
      const userFolderName = `${SYNC_CONFIG.APP_FOLDER_NAME}_${this.userId}`;
      
      const folder = await findOrCreateGoogleDriveFolder({
        accessToken: this.accessToken,
        folderName: userFolderName
      });
      
      this.appFolderId = folder.id;
    } catch (error) {
      secureLogger.error('Failed to setup app folder:', error);
      throw error;
    }
  }

  // Lightweight metadata-only check — no file content downloaded
  // Returns { id, modifiedTime, size } or null
  async getCloudFileMetadata() {
    try {
      if (!this.accessToken || !this.appFolderId) return null;
      if (!navigator.onLine) return null;

      // Fast path: direct lookup using cached fileId (1 API call, no search)
      const cachedId = this.backupFileId || this.getSyncMetadata().lastUploadFileId;
      if (cachedId) {
        const url = `https://www.googleapis.com/drive/v3/files/${cachedId}?fields=id,modifiedTime,size`;
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${this.accessToken}` }
        });
        if (response.ok) {
          const meta = await response.json();
          this.backupFileId = meta.id;
          return meta;
        }
        // File deleted or error — clear cache, fall through to search
        if (response.status === 404) {
          this.backupFileId = null;
          this.updateSyncMetadata({ lastUploadFileId: null });
        }
        if (response.status === 401) {
          this.clearAuthenticationState();
          return null;
        }
      }

      // Slow path: search by name (metadata only, no content)
      const query = `name='${SYNC_CONFIG.BACKUP_FILE_NAME}' and '${this.appFolderId}' in parents and trashed=false`;
      const searchUrl = `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=files(id,modifiedTime,size)&orderBy=modifiedTime desc&pageSize=1`;
      const searchResponse = await fetch(searchUrl, {
        headers: { Authorization: `Bearer ${this.accessToken}` }
      });

      if (!searchResponse.ok) {
        if (searchResponse.status === 401) {
          this.clearAuthenticationState();
        }
        return null;
      }

      const searchData = await searchResponse.json();
      if (searchData.files.length === 0) return null;

      const file = searchData.files[0];
      this.backupFileId = file.id;
      return file;
    } catch (error) {
      secureLogger.debug('Failed to get cloud file metadata:', { message: error.message });
      return null;
    }
  }

  // Get all local data for sync
  async getAllLocalData() {
    const data = {};
    const metadata = this.getSyncMetadata();

    // Image-carrying keys already store the compact {$ref:"<hash>"} shape
    // in IDB (see services/assetStorage). Read them raw — the blob ships
    // to Drive in compact form, and the paired _assets map (added below)
    // lets the downloader rehydrate on apply.
    for (const key of SYNC_KEYS) {
      try {
        const value = await localforage.getItem(key);
        // Convert null to undefined to avoid DataCloneError
        data[key] = value === null ? undefined : value;
      } catch (error) {
        secureLogger.warn(`Failed to read ${key}:`, error);
        data[key] = undefined;
      }
    }

    try {
      const assets = await localforage.getItem(ASSETS_KEY);
      if (assets && typeof assets === 'object') {
        data._assets = assets;
      }
    } catch (error) {
      secureLogger.warn('Failed to read _assets for upload:', error);
    }

    const now = Date.now();
    data._metadata = {
      timestamp: now,
      deviceId: metadata.deviceId,
      version: metadata.syncVersion,
      schemaVersion: CURRENT_SCHEMA_VERSION,
      hashVersion: HASH_VERSION,
      hash: await this.generateDataHash(data),
    };

    return data;
  }

  // Migrate a cloud blob from an older schema version forward.
  // No-op for v1 (current). When CURRENT_SCHEMA_VERSION is bumped, add
  // case branches here to translate older shapes.
  // Always returns a NEW object — never mutate the input.
  migrateCloudData(cloudData, fromVersion) {
    if (fromVersion === CURRENT_SCHEMA_VERSION) return cloudData;
    secureLogger.info(
      `Migrating cloud data from schema v${fromVersion} -> v${CURRENT_SCHEMA_VERSION}`
    );
    // No migrations defined yet. Return as-is and stamp the new version
    // so the next upload writes the current schema.
    const migrated = { ...cloudData };
    if (migrated._metadata) {
      migrated._metadata = { ...migrated._metadata, schemaVersion: CURRENT_SCHEMA_VERSION };
    }
    return migrated;
  }

  // Deterministic JSON serialization used by generateDataHash. Recursively
  // key-sorts objects so the output is independent of insertion order.
  // Dates are normalized to their ISO string form so the hash computed over
  // IDB (Date instances) matches the one computed after the Drive round-trip
  // (where JSON.parse leaves them as strings). `undefined` values on objects
  // are dropped to mirror JSON.stringify's behavior; arrays keep positional
  // undefineds as `null`, again mirroring JSON.
  _stableStringify(node) {
    if (node === null || node === undefined) return JSON.stringify(node);
    if (typeof node !== 'object') return JSON.stringify(node);
    if (node instanceof Date) return JSON.stringify(node.toISOString());
    if (Array.isArray(node)) {
      const parts = node.map((v) => (v === undefined ? 'null' : this._stableStringify(v)));
      return '[' + parts.join(',') + ']';
    }
    const keys = Object.keys(node).sort();
    const parts = [];
    for (const k of keys) {
      const v = node[k];
      if (v === undefined) continue;
      parts.push(JSON.stringify(k) + ':' + this._stableStringify(v));
    }
    return '{' + parts.join(',') + '}';
  }

  // SHA-256 hex digest over a stable, recursively key-sorted serialization.
  // See HASH_VERSION for why the v1 scheme (JSON.stringify with a replacer
  // array) was broken.
  async generateDataHash(data) {
    const jsonString = this._stableStringify(data);
    if (typeof crypto !== 'undefined' && crypto.subtle) {
      const buffer = new TextEncoder().encode(jsonString);
      const digest = await crypto.subtle.digest('SHA-256', buffer);
      const bytes = new Uint8Array(digest);
      let hex = '';
      for (let i = 0; i < bytes.length; i++) {
        hex += bytes[i].toString(16).padStart(2, '0');
      }
      return hex;
    }
    // Fallback for non-secure contexts: keep behavior, just don't crash.
    let hash = 0;
    for (let i = 0; i < jsonString.length; i++) {
      const char = jsonString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash.toString();
  }

  // Count rows on the key array entities. Used by the shrink-guard to decide
  // whether the pending upload is suspiciously smaller than the last one.
  _countRowsByKey(data) {
    const counts = {};
    for (const key of [INVOICES_KEY, INVOICE_DETAILS, CLIENTS_KEY, PRODUCTS_KEY, QUOTATIONS_KEY, QUOTATION_DETAILS]) {
      const v = data?.[key];
      counts[key] = Array.isArray(v) ? v.length : 0;
    }
    return counts;
  }

  // Decide whether the pending upload shrinks the cloud copy enough that we
  // should snapshot first. Returns { shrank: boolean, reasons: string[] }.
  //
  // Rules (any of):
  //   - a key that previously had >10 rows drops to zero
  //   - a key loses more than 50% of its rows AND that loss is >10 rows
  //
  // The archive partitioner naturally moves invoices out of the active set,
  // so we subtract `_archivedInvoiceIds.length` from the "expected" invoices
  // count to avoid false positives during migration.
  _detectSuspiciousShrink(newCounts) {
    const last = this.getSyncMetadata().lastUploadedRowCounts;
    if (!last) return { shrank: false, reasons: [] };

    const archivedCount = Array.isArray(this._lastArchivedIds)
      ? this._lastArchivedIds.length
      : 0;

    const reasons = [];
    for (const key of Object.keys(newCounts)) {
      const prev = last[key] ?? 0;
      let next = newCounts[key];
      // Invoice keys legitimately shrink when the partitioner moves paid
      // invoices into cold_storage — compensate so we don't fire falsely.
      if ((key === INVOICES_KEY || key === INVOICE_DETAILS) && archivedCount > 0) {
        next = next + archivedCount;
      }

      if (prev > 10 && next === 0) {
        reasons.push(`${key}: ${prev} → 0`);
      } else if (prev - next > 10 && next < prev * 0.5) {
        reasons.push(`${key}: ${prev} → ${newCounts[key]}`);
      }
    }
    return { shrank: reasons.length > 0, reasons };
  }

  // Upload data to Google Drive
  // Uses cached fileId to skip the search API call on subsequent syncs.
  // Recomputes _metadata.hash AFTER all blob modifications (_syncInfo,
  // _archivedInvoiceIds added by the partitioner) so verifyDataHash
  // succeeds on download.
  async uploadToCloud(data, syncType = 'manual') {
    try {
      // Rotate the previous backup once per BACKUP_ROTATION_INTERVAL_MS
      // so we always have one prior generation to recover from.
      // Failures are non-fatal — the main upload still proceeds.
      try {
        await this.rotateBackupIfDue();
      } catch (rotErr) {
        secureLogger.warn('Backup rotation skipped:', rotErr);
      }

      // Shrink guard: if this upload would drastically reduce the row counts
      // of critical entities (e.g. a buggy write produced empty arrays, or a
      // "Clear data" race persisted dirty deletes), snapshot the current
      // cloud copy FIRST so the drop is recoverable. Unlike rotateBackupIfDue
      // (time-bound once/day), this fires on-demand at the moment of risk.
      const newCounts = this._countRowsByKey(data);
      const shrinkCheck = this._detectSuspiciousShrink(newCounts);
      if (shrinkCheck.shrank) {
        secureLogger.warn(
          `Shrink guard: pending upload drops rows significantly — forcing snapshot. ${shrinkCheck.reasons.join(', ')}`
        );
        this.notifyListeners('shrink_detected', {
          reasons: shrinkCheck.reasons,
          timestamp: Date.now(),
        });
        try {
          const snap = await this.createVersionedSnapshot({ manual: false });
          if (snap) {
            this.pruneOldVersions().catch(e =>
              secureLogger.warn('Version prune failed after shrink-guard snapshot:', e?.message || e)
            );
            secureLogger.info(`Shrink-guard snapshot created: ${snap.name}`);
          }
        } catch (snapErr) {
          secureLogger.warn('Shrink-guard snapshot failed (continuing upload anyway):', snapErr);
        }
      }

      const backupData = {
        ...data,
        _syncInfo: {
          syncType,
          timestamp: Date.now(),
          deviceId: this.getSyncMetadata().deviceId,
        }
      };

      // Recompute the integrity hash over the FINAL upload blob.
      // Without this, the embedded hash from getAllLocalData wouldn't cover
      // _syncInfo / _archivedInvoiceIds and verifyDataHash would always fail
      // on download. hashVersion is re-stamped here too in case the caller
      // handed us a legacy blob without it.
      if (backupData._metadata) {
        const placeholderMeta = {
          ...backupData._metadata,
          hashVersion: HASH_VERSION,
          hash: HASH_PLACEHOLDER,
        };
        const hashable = { ...backupData, _metadata: placeholderMeta };
        const finalHash = await this.generateDataHash(hashable);
        backupData._metadata = {
          ...backupData._metadata,
          hashVersion: HASH_VERSION,
          hash: finalHash,
        };
      }

      const blob = new Blob([JSON.stringify(backupData, null, 2)], {
        type: 'application/json'
      });

      const fileName = SYNC_CONFIG.BACKUP_FILE_NAME;

      // Use cached fileId — only search if not cached
      let existingFileId = this.backupFileId;
      if (!existingFileId) {
        try {
          const searchQuery = `name='${fileName}' and '${this.appFolderId}' in parents and trashed=false`;
          const searchUrl = `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(searchQuery)}&fields=files(id,name)`;

          const searchResponse = await fetch(searchUrl, {
            headers: { Authorization: `Bearer ${this.accessToken}` }
          });

          if (searchResponse.ok) {
            const searchData = await searchResponse.json();
            if (searchData.files.length > 0) {
              existingFileId = searchData.files[0].id;
              this.backupFileId = existingFileId;
            }
          }
        } catch (error) {
          secureLogger.warn('Could not search for existing file:', error);
        }
      }

      let result;
      if (existingFileId) {
        // Update existing file — request modifiedTime in response
        const updateUrl = `https://www.googleapis.com/upload/drive/v3/files/${existingFileId}?uploadType=media&fields=id,modifiedTime`;
        const updateResponse = await fetch(updateUrl, {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: blob,
        });

        if (!updateResponse.ok) {
          // If file was deleted, clear cache and fall through to create
          if (updateResponse.status === 404) {
            this.backupFileId = null;
            existingFileId = null;
          } else {
            throw new Error('Failed to update existing file: ' + (await updateResponse.text()));
          }
        } else {
          result = await updateResponse.json();
          this.backupFileId = result.id;
        }
      }

      if (!result) {
        // Create new file
        result = await uploadToGoogleDrive({
          accessToken: this.accessToken,
          file: blob,
          fileName,
          folderId: this.appFolderId
        });
        this.backupFileId = result.id;

        // Fetch modifiedTime for the newly created file
        try {
          const metaUrl = `https://www.googleapis.com/drive/v3/files/${result.id}?fields=modifiedTime`;
          const metaResponse = await fetch(metaUrl, {
            headers: { Authorization: `Bearer ${this.accessToken}` }
          });
          if (metaResponse.ok) {
            const meta = await metaResponse.json();
            result.modifiedTime = meta.modifiedTime;
          }
        } catch (e) {
          // Non-critical — next sync will re-fetch
        }
      }

      // Update sync metadata with modifiedTime for future comparisons.
      // lastUploadedRowCounts feeds the shrink guard on the NEXT upload.
      this.updateSyncMetadata({
        lastSyncTime: Date.now(),
        lastSyncHash: data._metadata.hash,
        lastUploadFileId: result.id,
        lastCloudModifiedTime: result.modifiedTime || null,
        lastUploadedRowCounts: newCounts,
      });

      return result;
    } catch (error) {
      secureLogger.error('Failed to upload data:', error);
      throw error;
    }
  }

  // Download data from Google Drive
  // Uses cached fileId to skip the search API call
  async downloadFromCloud() {
    try {
      if (!this.accessToken || !this.appFolderId) {
        secureLogger.debug('Cloud sync not initialized, skipping download');
        return null;
      }

      if (!navigator.onLine) {
        secureLogger.debug('Device is offline, skipping cloud download');
        return null;
      }

      let fileId = this.backupFileId;
      let modifiedTime = null;

      if (!fileId) {
        // Search for backup file (only when no cached ID)
        const query = `name='${SYNC_CONFIG.BACKUP_FILE_NAME}' and '${this.appFolderId}' in parents and trashed=false`;
        const searchUrl = `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=files(id,name,modifiedTime)&orderBy=modifiedTime desc`;

        const searchResponse = await fetch(searchUrl, {
          headers: { Authorization: `Bearer ${this.accessToken}` }
        });

        if (!searchResponse.ok) {
          if (searchResponse.status === 401) {
            secureLogger.debug('Access token expired, clearing auth state');
            this.clearAuthenticationState();
            return null;
          }
          throw new Error(`Failed to search for backup file: ${searchResponse.status} ${searchResponse.statusText}`);
        }

        const searchData = await searchResponse.json();

        if (searchData.files.length === 0) {
          secureLogger.debug('No backup file found in cloud');
          return null;
        }

        fileId = searchData.files[0].id;
        modifiedTime = searchData.files[0].modifiedTime;
        this.backupFileId = fileId;
      }

      // Download file content directly using fileId
      const downloadUrl = `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`;
      const downloadResponse = await fetch(downloadUrl, {
        headers: { Authorization: `Bearer ${this.accessToken}` }
      });

      if (!downloadResponse.ok) {
        if (downloadResponse.status === 404) {
          // File deleted — clear cache
          this.backupFileId = null;
          return null;
        }
        throw new Error(`Failed to download backup file: ${downloadResponse.status} ${downloadResponse.statusText}`);
      }

      const backupData = await downloadResponse.json();

      return {
        data: backupData,
        fileId,
        modifiedTime,
      };
    } catch (error) {
      secureLogger.debug('Failed to download from cloud:', {
        message: error.message,
        hasToken: !!this.accessToken,
        hasFolder: !!this.appFolderId,
        isOnline: navigator.onLine
      });
      // Don't throw error for optional cloud sync functionality
      return null;
    }
  }

  // Apply cloud data to local storage.
  //
  // Bumps cloudApplyDepth (re-entrant counter) so data-change hooks ignore
  // writes triggered by this function. Decrements in finally — including
  // on early returns (the previous boolean implementation leaked a stuck-true
  // state on every "local_newer" skip, silently disabling dirty tracking).
  //
  // Concurrent-edit safety for the "local is newer" branch of conflict
  // resolution. We still need to pull cloud rows whose per-row updatedAt is
  // newer than the corresponding local row (edits made on another device),
  // otherwise those edits get clobbered when we upload local verbatim.
  //
  // Only touches MERGEABLE_ARRAY_KEYS. Non-mergeable keys (forms, defaults,
  // company metadata) are single-object and local-newer semantics are
  // correct for them — untouched here.
  //
  // Returns { key, localCount, cloudCount, mergedCount, pulled }[] for logging.
  // `pulled` counts rows whose id was absent locally or whose cloud updatedAt
  // was higher — the rows this merge rescued from being overwritten.
  async _mergeMergeableKeysFromCloud(cloudData) {
    const reports = [];
    for (const key of SYNC_KEYS) {
      if (!MERGEABLE_ARRAY_KEYS.has(key)) continue;
      const cloudRows = cloudData?.[key];
      if (!Array.isArray(cloudRows) || cloudRows.length === 0) continue;

      const localFull = (await localforage.getItem(key)) || [];
      const localArr = Array.isArray(localFull) ? localFull : [];

      // Count rows cloud is contributing (new id, or newer updatedAt than local)
      const localById = new Map();
      for (const r of localArr) if (r && r.id != null) localById.set(r.id, r);
      let pulled = 0;
      for (const r of cloudRows) {
        if (!r || r.id == null) continue;
        const existing = localById.get(r.id);
        if (!existing) { pulled++; continue; }
        const ct = Number(r.updatedAt) || 0;
        const lt = Number(existing.updatedAt) || 0;
        if (ct > lt) pulled++;
        else if (ct === lt && r._deleted && !existing._deleted) pulled++;
      }

      const merged = mergeRowsById(localArr, cloudRows);
      // Only write if the merge changed the local state. mergeRowsById is
      // deterministic, so "same length AND no pulls" is a safe no-op signal.
      if (pulled > 0 || merged.length !== localArr.length) {
        if (isAssetKey(key)) {
          await compactingSetItem(key, merged);
        } else {
          await localforage.setItem(key, merged);
        }
      }
      reports.push({
        key,
        localCount: localArr.length,
        cloudCount: cloudRows.length,
        mergedCount: merged.length,
        pulled,
      });
    }
    return reports;
  }

  // Verifies the cloud blob's embedded SHA-256 hash before touching local
  // data. On mismatch, refuses to apply (possible corruption / tampering).
  async applyCloudDataToLocal(cloudData, conflictResolution = 'latest_wins') {
    const metadata = this.getSyncMetadata();
    // `force` = manual restore (file import or versioned-snapshot rollback).
    // Semantics: full replace, bypass hash gating (user may have legacy or
    // hand-edited blobs), bypass per-row merge (imported rows must win even
    // if local updatedAt is newer — otherwise restore looks like a no-op).
    const isForce = conflictResolution === 'force';

    // Integrity check: re-hash the blob (with hash field swapped to a
    // placeholder) and compare to the embedded hash. Legacy blobs without
    // a hash field pass through.
    const hashOk = await this.verifyDataHash(cloudData);
    if (!hashOk) {
      if (isForce) {
        secureLogger.warn('Hash mismatch on force apply — continuing (manual restore)');
      } else {
        secureLogger.error('Cloud data hash mismatch — refusing to apply (possible corruption)');
        this.notifyListeners('sync_failed', {
          type: 'apply',
          stage: 'apply.hash',
          errorCode: 'hash_mismatch',
          error: 'hash_mismatch',
          timestamp: Date.now(),
        });
        return { applied: false, reason: 'hash_mismatch' };
      }
    }

    // Schema version gate. Newer-than-current = refuse (old client must not
    // downgrade fresh data). Older = run forward through the migrator before
    // touching local storage. Legacy blobs without the field are treated as v1.
    const incomingVersion = cloudData?._metadata?.schemaVersion ?? 1;
    if (incomingVersion > CURRENT_SCHEMA_VERSION) {
      secureLogger.error(
        `Cloud schema v${incomingVersion} > supported v${CURRENT_SCHEMA_VERSION} — refusing to apply (client likely outdated)`
      );
      this.notifyListeners('sync_failed', {
        type: 'apply',
        stage: 'apply.schema',
        errorCode: 'schema_too_new',
        error: 'schema_too_new',
        cloudVersion: incomingVersion,
        supportedVersion: CURRENT_SCHEMA_VERSION,
        timestamp: Date.now(),
      });
      return { applied: false, reason: 'schema_too_new' };
    }
    if (incomingVersion < CURRENT_SCHEMA_VERSION) {
      cloudData = this.migrateCloudData(cloudData, incomingVersion);
    }

    // Shape guard: scan incoming array entities for structural corruption
    // (missing ids, flattened Date fields, etc.) BEFORE writing. If any key
    // exceeds CORRUPTION_REFUSAL_RATIO we refuse the entire apply — a
    // single-key corruption usually means the uploading device had a Date-
    // flatten-class bug and every entity shares the same root cause.
    //
    // Force mode (manual restore, versioned-snapshot rollback) is advisory
    // only — the user explicitly chose this payload, so we log but never
    // block. Refusing would make the recovery UI feel broken.
    const shapeReports = [];
    for (const key of SYNC_KEYS) {
      if (cloudData[key] === undefined) continue;
      if (!isShapeCheckableKey(key)) continue;
      const report = validateCollection(key, cloudData[key]);
      if (report.checked && report.invalid > 0) shapeReports.push(report);
    }
    const firstCorrupted = shapeReports.find((r) => r.corrupted);
    if (firstCorrupted) {
      if (isForce) {
        secureLogger.warn(
          `Shape violations on force apply — continuing. ${shapeReports
            .map((r) => `${r.key}:${r.invalid}/${r.total}`)
            .join(', ')}`,
          { samples: firstCorrupted.samples }
        );
      } else {
        secureLogger.error(
          `Cloud data shape violation — refusing to apply. ${shapeReports
            .map((r) => `${r.key}:${r.invalid}/${r.total}`)
            .join(', ')}`,
          { samples: firstCorrupted.samples }
        );
        this.notifyListeners('sync_failed', {
          type: 'apply',
          stage: 'apply.shape',
          errorCode: 'shape_violation',
          error: 'shape_violation',
          reports: shapeReports.map((r) => ({
            key: r.key,
            invalid: r.invalid,
            total: r.total,
            ratio: r.ratio,
          })),
          timestamp: Date.now(),
        });
        return { applied: false, reason: 'shape_violation' };
      }
    } else if (shapeReports.length > 0) {
      // Under-threshold violations are logged but apply proceeds.
      secureLogger.warn(
        `Shape violations below refusal threshold — applying. ${shapeReports
          .map((r) => `${r.key}:${r.invalid}/${r.total}`)
          .join(', ')}`
      );
    }

    // Compact backups ship with an `_assets` map + { $ref: "<sha256>" }
    // placeholders in place of repeated base64 blobs (logo, seal, bg).
    // Union any incoming assets into local IDB BEFORE writing the keys
    // that reference them so refs never dangle. Readers (assetStorage.getItem)
    // expand on the way out to Redux, so downstream consumers are unaware.
    // Legacy (fat) blobs have no `_assets` — this is a no-op for them.
    if (cloudData && cloudData._assets && typeof cloudData._assets === 'object') {
      try {
        await mergeIncomingAssets(cloudData._assets);
      } catch (err) {
        secureLogger.warn('Failed to merge incoming _assets:', err);
      }
    }

    secureLogger.info('Applying cloud data to local storage');
    this.cloudApplyDepth++;
    try {
      // Simple conflict resolution: latest wins
      if (conflictResolution === 'latest_wins') {
        const cloudTimestamp = cloudData._metadata?.timestamp || 0;
        const localTimestamp = metadata.lastSyncTime || 0;

        if (cloudTimestamp <= localTimestamp) {
          secureLogger.info('Local data is newer or equal, skipping sync');
          return { applied: false, reason: 'local_newer' };
        }
      }

      // Apply cloud data to local storage
      const applied = [];
      const failed = [];

      // Archived invoice IDs embedded by the partitioner — preserve these locally
      const archivedInvoiceIds = new Set(cloudData._archivedInvoiceIds || []);

      for (const key of SYNC_KEYS) {
        try {
          if (cloudData[key] !== undefined) {
            let dataToApply = cloudData[key];

            // For invoice keys: preserve locally-held archived invoices that were
            // excluded from the active backup (they live in cold_storage/ on Drive)
            if ((key === INVOICES_KEY || key === INVOICE_DETAILS) && archivedInvoiceIds.size > 0) {
              const localData = await localforage.getItem(key) || [];
              const cloudIds = new Set(dataToApply.map(item => item.id));
              const localArchived = localData.filter(
                item => archivedInvoiceIds.has(item.id) && !cloudIds.has(item.id)
              );
              if (localArchived.length > 0) {
                dataToApply = [...dataToApply, ...localArchived];
                secureLogger.info(`Preserved ${localArchived.length} archived items for ${key}`);
              }
            }

            // Per-row merge for array entities: keep the row whose updatedAt
            // is newest (tombstones included). Without this, a sync from
            // device B would overwrite an unrelated edit made on device A.
            // Other keys (forms, defaults, company) are single-object —
            // replace-behavior is correct.
            //
            // Force mode (manual restore) skips the merge: the user explicitly
            // asked to roll back, so imported rows must win even if locals are
            // newer. Otherwise a restore looks like a no-op.
            if (!isForce && MERGEABLE_ARRAY_KEYS.has(key) && Array.isArray(dataToApply)) {
              const localFull = (await localforage.getItem(key)) || [];
              const before = dataToApply.length;
              dataToApply = mergeRowsById(localFull, dataToApply);
              secureLogger.info(
                `Merged ${key}: cloud=${before}, local=${Array.isArray(localFull) ? localFull.length : 0}, merged=${dataToApply.length}`
              );
            }

            // NOTE: subscription limits are enforced at write/UI time via
            // UsageRestriction components — never truncate during cloud apply.
            // Truncating here would persist the smaller array locally and then
            // re-upload it, permanently destroying the overflow rows.

            // Route image-carrying writes through the compacting wrapper so
            // data from legacy (fat) cloud blobs lands deduped. Compact cloud
            // data passes through the fast idempotent path (no big strings
            // found → nothing to hash).
            if (isAssetKey(key)) {
              await compactingSetItem(key, dataToApply);
            } else {
              await localforage.setItem(key, dataToApply);
            }
            applied.push(key);
            secureLogger.info(`Applied ${key} from cloud`);
          }
        } catch (error) {
          secureLogger.error(`Failed to apply ${key}:`, error);
          failed.push({ key, error: error.message });
        }
      }

      // Update metadata
      this.updateSyncMetadata({
        lastSyncTime: Date.now(),
        lastSyncHash: cloudData._metadata?.hash,
      });

      secureLogger.info(`Sync completed: ${applied.length} applied, ${failed.length} failed`);

      return {
        applied: true,
        appliedKeys: applied,
        failedKeys: failed,
        timestamp: Date.now(),
      };
    } finally {
      this.cloudApplyDepth = Math.max(0, this.cloudApplyDepth - 1);
    }
  }

  // Re-hash the blob (excluding the hash field itself) and compare to the
  // embedded _metadata.hash. Returns true for:
  //   - legacy blobs missing the hash field (first sync after upgrade)
  //   - v1-hash blobs (the old scheme was broken — see HASH_VERSION. Refusing
  //     those would strand users on the first upgraded download. They re-stamp
  //     v2 on the next upload.)
  // Actively verifies v2+ blobs.
  async verifyDataHash(cloudData) {
    const embedded = cloudData?._metadata?.hash;
    if (!embedded) return true;
    const incomingHashVersion = cloudData?._metadata?.hashVersion ?? 1;
    if (incomingHashVersion < HASH_VERSION) {
      secureLogger.debug(
        `Skipping hash verify: blob hashVersion v${incomingHashVersion} < current v${HASH_VERSION} (legacy scheme)`
      );
      return true;
    }
    if (incomingHashVersion > HASH_VERSION) {
      // Newer scheme than we know — can't verify. Treat as pass rather than
      // refuse; the schemaVersion gate elsewhere handles forward-incompat.
      secureLogger.warn(
        `Blob hashVersion v${incomingHashVersion} > supported v${HASH_VERSION} — skipping verify`
      );
      return true;
    }
    const meta = { ...cloudData._metadata, hash: HASH_PLACEHOLDER };
    const blob = { ...cloudData, _metadata: meta };
    const computed = await this.generateDataHash(blob);
    return computed === embedded;
  }

  // True when IDB is missing every key a working account must have. Used as a
  // disaster-recovery trigger: if local is empty but a cloud backup exists,
  // run a full restore instead of deferring to timestamp-based conflict logic
  // (which would often misclassify the empty local state as "newer").
  //
  // Reads five keys (cheap) — no need to scan every SYNC_KEY. If *any* of
  // these has content, the account is not empty.
  async isLocalDataEffectivelyEmpty() {
    const probeKeys = [
      INVOICES_KEY,
      CLIENTS_KEY,
      PRODUCTS_KEY,
      COMPANY_KEY,
      QUOTATIONS_KEY,
    ];
    for (const key of probeKeys) {
      try {
        const v = await localforage.getItem(key);
        if (v == null) continue;
        if (Array.isArray(v)) {
          if (v.length > 0) return false;
        } else if (typeof v === 'object') {
          if (Object.keys(v).length > 0) return false;
        } else if (v) {
          return false;
        }
      } catch (err) {
        // Read failure — don't assume empty; skip the DR path rather than
        // risk overwriting a partially-readable local with cloud.
        secureLogger.warn(`isLocalDataEffectivelyEmpty: failed to read ${key}`, err);
        return false;
      }
    }
    return true;
  }

  // Disaster-recovery: download active backup + cold-storage, apply to an
  // empty local IDB. Called from performSync when isLocalDataEffectivelyEmpty()
  // returns true AND a cloud backup exists.
  //
  // Semantics:
  //   - Apply runs with 'force' to bypass the latest_wins timestamp guard,
  //     which is meaningless on an empty local. The force path inside
  //     applyCloudDataToLocal also downgrades hash-mismatch from fatal to a
  //     warning — critical here because refusing to restore leaves the user
  //     with no data at all (an empty local IS the worst possible state).
  //   - Archive (cold_storage) is merged too — this is the scenario that
  //     motivated the cold-storage system in the first place.
  //   - dirtyKeys is cleared on success so the next sync doesn't re-upload
  //     the just-restored payload.
  async _restoreFromCloudFresh() {
    try {
      const cloudResult = await this.downloadFromCloud();
      if (!cloudResult) {
        secureLogger.warn('Full restore aborted — cloud download returned null');
        return { success: false, reason: 'download_failed' };
      }

      const applyResult = await this.applyCloudDataToLocal(cloudResult.data, 'force');
      if (!applyResult?.applied) {
        return { success: false, reason: applyResult?.reason || 'apply_refused' };
      }

      this.updateSyncMetadata({ lastCloudModifiedTime: cloudResult.modifiedTime });

      try {
        const archive = await this.downloadArchiveFromCloud();
        if (archive) await this.mergeArchiveIntoLocal(archive);
      } catch (err) {
        secureLogger.warn('Archive restore during full-restore skipped:', err);
      }

      this.dirtyKeys.clear();
      this.persistSyncStateSoon();

      secureLogger.info('Full restore from cloud completed (empty-local path)');
      return { success: true };
    } catch (error) {
      secureLogger.error('Full restore failed:', error);
      return { success: false, reason: error.message };
    }
  }

  // Perform sync with incremental optimization
  // Fast paths avoid unnecessary downloads/uploads when nothing changed
  async performSync(syncType = 'manual', options = {}) {
    // forceCompare: skip the metadata-only fast paths and route straight to an
    // explicit latest-wins comparison between cloud's internal timestamp and
    // local's. Used by the manual force-sync button so direction (upload vs.
    // download) is always decided by comparing actual data timestamps, not by
    // a cached modified-time shortcut.
    const { forceCompare = false } = options;

    if (this.syncInProgress) {
      secureLogger.info('Sync already in progress, skipping');
      return { success: false, reason: 'sync_in_progress' };
    }

    if (!this.isOnline) {
      secureLogger.info('Offline, queuing sync operation');
      this.queueSyncOperation(syncType);
      return { success: false, reason: 'offline' };
    }

    if (!this.accessToken) {
      secureLogger.info('No access token available');
      return { success: false, reason: 'no_token' };
    }

    // Proactive: if the stored token is within the expiring-soon window
    // (default 5 min before TTL), broadcast a refresh request now so a
    // listener can silently re-prompt sign-in before this request 401s.
    if (isStoredTokenExpiringSoon()) {
      requestTokenRefresh('pre_sync');
    }

    this.syncInProgress = true;
    this.notifyListeners('sync_started', { type: syncType });

    try {
      const hasDirtyKeys = this.dirtyKeys.size > 0;
      const metadata = this.getSyncMetadata();

      // Step 1: Lightweight metadata-only check (no file content downloaded)
      const cloudMeta = await this.getCloudFileMetadata();
      const storedCloudModifiedTime = metadata.lastCloudModifiedTime;
      const cloudChanged = cloudMeta && cloudMeta.modifiedTime !== storedCloudModifiedTime;

      // ── Disaster-recovery path: local IDB is empty but a cloud backup exists ──
      // Triggered on a fresh install, after "Clear site data" in the browser, or
      // when the IDB store was wiped independent of localStorage. Without this
      // check, stale metadata (lastSyncTime / lastCloudModifiedTime) would cause
      // the normal sync logic to skip the download or refuse the apply as
      // "local_newer" — leaving the user with only cold-storage data.
      // Cheap probe (5 small reads) so it's safe to run on every sync.
      if (cloudMeta && !hasDirtyKeys) {
        const localEmpty = await this.isLocalDataEffectivelyEmpty();
        if (localEmpty) {
          secureLogger.info('Local IDB empty but cloud backup exists — running full restore');
          const restoreResult = await this._restoreFromCloudFresh();
          this.notifyListeners(restoreResult.success ? 'sync_completed' : 'sync_failed', {
            type: syncType,
            action: 'full_restore',
            success: restoreResult.success,
            reason: restoreResult.reason,
            timestamp: Date.now(),
          });
          return restoreResult.success
            ? { success: true, action: 'full_restore' }
            : { success: false, action: 'full_restore', reason: restoreResult.reason };
        }
      }

      // Fast paths rely on `cloudChanged` (a cached modified-time comparison)
      // to skip a full download. Force-sync bypasses them so direction is
      // decided by an explicit data-timestamp comparison downstream.
      if (!forceCompare) {
        // ── Fast path: nothing changed anywhere ──
        if (!hasDirtyKeys && !cloudChanged && cloudMeta) {
          secureLogger.info('No changes detected (metadata-only check), skipping sync');
          this.notifyListeners('sync_completed', {
            type: syncType, action: 'no_change', success: true
          });
          return { success: true, action: 'no_change' };
        }

        // ── Fast path: only local changed, cloud untouched ──
        if (hasDirtyKeys && !cloudChanged && cloudMeta) {
          secureLogger.info(`Local changes in [${[...this.dirtyKeys].join(', ')}], uploading`);
          const localData = await this.getAllLocalData();
          await this._uploadPartitioned(localData, syncType);
          this.dirtyKeys.clear();
          this.persistSyncStateSoon();
          this.notifyListeners('sync_completed', {
            type: syncType, action: 'upload_local', success: true
          });
          return { success: true, action: 'upload_local' };
        }
      }

      // ── No cloud file yet — initial upload ──
      if (!cloudMeta) {
        secureLogger.info('No cloud data found, uploading local data');
        const localData = await this.getAllLocalData();
        await this._uploadPartitioned(localData, syncType);
        this.dirtyKeys.clear();
        this.persistSyncStateSoon();
        this.notifyListeners('sync_completed', {
          type: syncType, action: 'upload_initial', success: true
        });
        return { success: true, action: 'upload_initial' };
      }

      // ── Fast path: only cloud changed, no local edits ──
      if (!forceCompare && cloudChanged && !hasDirtyKeys) {
        secureLogger.info('Cloud data changed, downloading');
        const cloudResult = await this.downloadFromCloud();
        if (cloudResult) {
          const applyResult = await this.applyCloudDataToLocal(cloudResult.data);

          // Only advance cloud-modified-time and run archive merge when the
          // active apply actually landed. Previous code ran the archive merge
          // unconditionally — if applyCloudDataToLocal silently refused
          // (hash_mismatch / schema_too_new / local_newer), the user ended
          // up with ONLY cold-storage invoices on top of stale/empty local
          // data. See docs/bug: archive-only-restore.
          if (applyResult?.applied) {
            this.updateSyncMetadata({ lastCloudModifiedTime: cloudResult.modifiedTime });

            if (syncType === 'initialize') {
              this.mergeArchiveIntoLocal(await this.downloadArchiveFromCloud()).catch(e =>
                secureLogger.warn('Archive restore during init skipped:', e)
              );
            }

            this.notifyListeners('sync_completed', {
              type: syncType, action: 'download_applied', success: true
            });
            return { success: true, action: 'download_applied' };
          }

          secureLogger.warn(
            `Cloud apply refused (${applyResult?.reason || 'unknown'}) — skipping archive merge`
          );
          this.notifyListeners('sync_failed', {
            type: syncType,
            stage: 'resolver.download_only',
            errorCode: 'apply_refused',
            error: `apply_refused:${applyResult?.reason || 'unknown'}`,
            applyReason: applyResult?.reason || 'unknown',
            timestamp: Date.now(),
          });
          return { success: false, action: 'apply_refused', reason: applyResult?.reason };
        }
      }

      // ── Full conflict resolution ──
      // Reached when both sides changed, OR on force-sync (which bypasses the
      // metadata-only fast paths and routes here unconditionally).
      secureLogger.info(
        forceCompare
          ? 'Force sync: resolving direction via explicit timestamp comparison'
          : 'Both local and cloud changed, resolving conflicts'
      );
      let localData = await this.getAllLocalData();
      const cloudResult = await this.downloadFromCloud();

      if (!cloudResult) {
        // Cloud file disappeared — upload local
        await this._uploadPartitioned(localData, syncType);
        this.dirtyKeys.clear();
        this.persistSyncStateSoon();
        this.notifyListeners('sync_completed', {
          type: syncType, action: 'upload_initial', success: true
        });
        return { success: true, action: 'upload_initial' };
      }

      // Timestamp-based conflict resolution. localTimestamp is the last successful
      // apply/upload timestamp; if dirty keys exist we treat local as definitely
      // post-sync so "now" is the floor on its freshness.
      const cloudTimestamp = cloudResult.data._metadata?.timestamp || 0;
      const localTimestamp = hasDirtyKeys
        ? Math.max(metadata.lastSyncTime || 0, Date.now())
        : (metadata.lastSyncTime || 0);

      secureLogger.info(
        `Conflict resolution: cloudTs=${cloudTimestamp}, localTs=${localTimestamp}, dirty=${hasDirtyKeys}`
      );

      if (cloudTimestamp > localTimestamp) {
        // Cloud newer — apply (per-row merge keeps any local rows with newer updatedAt),
        // then re-upload the merged result so cloud reflects the combined state.
        secureLogger.info('Cloud data is newer, applying and merging local changes');
        const applyResult = await this.applyCloudDataToLocal(cloudResult.data);

        if (!applyResult?.applied) {
          // schema_too_new: cloud came from a newer client — never overwrite it.
          if (applyResult?.reason === 'schema_too_new') {
            this.notifyListeners('sync_failed', {
              type: syncType,
              stage: 'resolver.schema',
              errorCode: 'schema_too_new',
              error: 'schema_too_new',
              timestamp: Date.now(),
            });
            return { success: false, action: 'apply_refused', reason: 'schema_too_new' };
          }
          // hash_mismatch / local_newer: cloud suspect or disagreed with our own
          // metadata. Uploading local as recovery turns this device into the new
          // source of truth rather than leaving the user stuck.
          secureLogger.warn(
            `Cloud apply refused (${applyResult?.reason || 'unknown'}) despite cloudTs > localTs — uploading local as recovery`
          );
          await this._uploadPartitioned(localData, syncType);
          this.dirtyKeys.clear();
          this.persistSyncStateSoon();
          this.notifyListeners('sync_completed', {
            type: syncType, action: 'upload_local', success: true, recovered: applyResult?.reason,
          });
          return { success: true, action: 'upload_local', recovered: applyResult?.reason };
        }

        const mergedData = await this.getAllLocalData();
        await this._uploadPartitioned(mergedData, syncType);
        this.dirtyKeys.clear();
        this.persistSyncStateSoon();
        this.notifyListeners('sync_completed', {
          type: syncType, action: 'download_applied', success: true, result: applyResult
        });
        return { success: true, action: 'download_applied', result: applyResult };
      } else if (cloudTimestamp < localTimestamp || hasDirtyKeys) {
        // Local newer, or we have unsynced local changes — upload.
        //
        // Concurrent-edit safety: before uploading, pull cloud-side row edits
        // whose per-row updatedAt is newer than local (device B edited client X
        // while device A edited client Y — the coarse localTimestamp > cloudTimestamp
        // check alone would clobber X). Only mergeable array keys are affected;
        // single-object keys (forms, defaults, company) keep local semantics.
        this.cloudApplyDepth++;
        let mergeReports = [];
        try {
          mergeReports = await this._mergeMergeableKeysFromCloud(cloudResult.data);
        } catch (mergeErr) {
          secureLogger.warn('Concurrent-edit merge failed (continuing with local upload):', mergeErr);
        } finally {
          this.cloudApplyDepth = Math.max(0, this.cloudApplyDepth - 1);
        }
        const totalPulled = mergeReports.reduce((s, r) => s + r.pulled, 0);
        if (totalPulled > 0) {
          secureLogger.info(
            `Pulled ${totalPulled} cloud row edits into local before upload: ${mergeReports
              .filter(r => r.pulled > 0)
              .map(r => `${r.key}(+${r.pulled})`)
              .join(', ')}`
          );
          // Re-read local so the upload reflects the merged state
          localData = await this.getAllLocalData();
        } else {
          secureLogger.info('Local data is newer, no cloud-side edits to pull');
        }
        await this._uploadPartitioned(localData, syncType);
        this.dirtyKeys.clear();
        this.persistSyncStateSoon();
        this.notifyListeners('sync_completed', {
          type: syncType,
          action: 'upload_local',
          success: true,
          pulled: totalPulled,
        });
        return { success: true, action: 'upload_local', pulled: totalPulled };
      } else {
        // Timestamps equal and no dirty keys — already in sync (only reachable via
        // force-sync; the metadata-only fast path handles this before downloading
        // during normal/auto syncs).
        secureLogger.info('Force sync: already in sync, no action needed');
        this.notifyListeners('sync_completed', {
          type: syncType, action: 'no_change', success: true
        });
        return { success: true, action: 'no_change' };
      }

    } catch (error) {
      secureLogger.debug('Sync failed (cloud sync is optional):', {
        message: error.message,
        syncType,
        hasToken: !!this.accessToken,
        hasFolder: !!this.appFolderId,
        isOnline: navigator.onLine
      });

      this.notifyListeners('sync_failed', {
        type: syncType,
        stage: 'resolver.exception',
        errorCode: this._normalizeErrorCode(error),
        error: error.message,
        timestamp: Date.now(),
      });

      if (syncType === 'auto' || syncType === 'data_change') {
        this.queueSyncOperation('retry');
      }

      return { success: false, error: error.message };
    } finally {
      this.syncInProgress = false;
    }
  }

  // Queue sync operation for when online
  queueSyncOperation(syncType) {
    this.syncQueue.push({
      type: syncType,
      timestamp: Date.now(),
    });
    this.persistSyncStateSoon();
  }

  // Process queued sync operations
  async processQueuedSyncOperations() {
    if (this.syncQueue.length === 0) return;

    secureLogger.info(`Processing ${this.syncQueue.length} queued sync operations`);

    // Get latest queued operation
    const latestOperation = this.syncQueue.pop();
    this.syncQueue = []; // Clear queue
    this.persistSyncStateSoon();

    try {
      await this.performSync(latestOperation.type);
    } catch (error) {
      secureLogger.error('Failed to process sync queue:', error);
    }
  }

  // Force sync (manual trigger) — bypass metadata-only fast paths so direction
  // (upload vs. download) is decided by an explicit timestamp comparison, not
  // by a cached modified-time. Answers the "who has the latest changes" question
  // the button is semantically asking.
  async forceSync() {
    secureLogger.info('Force sync triggered — explicit latest-wins comparison');
    return await this.performSync('manual', { forceCompare: true });
  }

  // Event listener management
  addEventListener(callback) {
    this.eventListeners.add(callback);
  }

  removeEventListener(callback) {
    this.eventListeners.delete(callback);
  }

  notifyListeners(event, data = {}) {
    // Telemetry: every sync_failed is appended to a bounded ring buffer + a
    // per-stage counter so the UI can show "3 hash_mismatch today" without
    // needing a server-side log pipeline. Stage defaults to 'unknown' when
    // a caller forgot to specify — flag those in the log so we can fix them.
    if (event === 'sync_failed') {
      const stage = data.stage || 'unknown';
      const entry = {
        timestamp: data.timestamp || Date.now(),
        type: data.type || 'unknown',
        stage,
        errorCode: data.errorCode || this._normalizeErrorCode(data.error),
        error: typeof data.error === 'string' ? data.error : (data.error?.message || 'unknown'),
      };
      this.recentFailures.push(entry);
      if (this.recentFailures.length > RECENT_FAILURES_CAP) {
        this.recentFailures.splice(0, this.recentFailures.length - RECENT_FAILURES_CAP);
      }
      this.failureCountsByStage[stage] = (this.failureCountsByStage[stage] || 0) + 1;
      if (stage === 'unknown') {
        secureLogger.warn('sync_failed event missing stage field', { event, error: entry.error });
      }
    }

    this.eventListeners.forEach(callback => {
      try {
        if (typeof callback === 'function') {
          callback(event, data);
        }
      } catch (error) {
        secureLogger.error('Error in sync event listener:', {
          error: error.message || 'Unknown error',
          event,
          stack: error.stack
        });
      }
    });
  }

  // Normalize free-form error strings into a small enum of codes. Callers
  // that already pass `errorCode` bypass this. Everything else falls through
  // to 'error' so we never lose an entry to a mismatched string.
  _normalizeErrorCode(err) {
    const msg = (typeof err === 'string' ? err : err?.message || '').toLowerCase();
    if (!msg) return 'unknown';
    if (msg.includes('hash_mismatch') || msg.includes('hash mismatch')) return 'hash_mismatch';
    if (msg.includes('schema_too_new')) return 'schema_too_new';
    if (msg.includes('shape_violation')) return 'shape_violation';
    if (msg.includes('apply_refused')) return 'apply_refused';
    if (msg.includes('local_newer')) return 'local_newer';
    if (msg.includes('network') || msg.includes('fetch') || msg.includes('failed to fetch')) return 'network';
    if (msg.includes('401') || msg.includes('unauthorized') || msg.includes('auth')) return 'auth';
    if (msg.includes('quota') || msg.includes('storage full')) return 'quota';
    return 'error';
  }

  // Telemetry accessor. Returns a cloned snapshot so callers can't mutate
  // the internal buffer. Includes the per-stage counters so the UI can
  // aggregate "3 hash_mismatch today" without rescanning the buffer.
  getRecentFailures() {
    return {
      failures: this.recentFailures.slice(),
      countsByStage: { ...this.failureCountsByStage },
    };
  }

  // Export data for manual backup
  async exportBackup() {
    try {
      const data = await this.getAllLocalData();
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json'
      });
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `logislip_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      secureLogger.info('Backup exported successfully');
      return true;
    } catch (error) {
      secureLogger.error('Failed to export backup:', error);
      throw error;
    }
  }

  // Import data from backup file
  //
  // Gotchas this handles (regression history):
  //   1. Legacy / hand-edited exports may lack `_metadata`. We stamp a minimal
  //      one so the schema + hash checks downstream don't reject a valid file.
  //   2. `force` routes through applyCloudDataToLocal without hash gating and
  //      without per-row merge (see isForce there). Without skipping merge,
  //      an import would be silently discarded because local updatedAt is
  //      almost always newer than the backup's.
  //   3. After a successful import the next sync MUST push (not pull), else
  //      the stale Drive copy would overwrite the restore within minutes.
  //      We null lastCloudModifiedTime + lastSyncHash and mark every sync
  //      key dirty so the next sync takes the "upload_local" branch.
  async importBackup(file) {
    try {
      const text = await file.text();
      const data = JSON.parse(text);

      if (!data || typeof data !== 'object') {
        throw new Error('Invalid backup file: not a JSON object');
      }

      // Accept legacy exports that lack _metadata — stamp a minimal one so
      // the schema/hash checks treat it as a v1 restore candidate.
      if (!data._metadata) {
        secureLogger.warn('Import backup missing _metadata — treating as legacy export');
        data._metadata = {
          timestamp: Date.now(),
          schemaVersion: 1,
        };
      }

      // Quick sanity: at least one known data key must be present
      const hasAnyKey = SYNC_KEYS.some(k => data[k] !== undefined);
      if (!hasAnyKey) {
        throw new Error('Invalid backup file: no recognized data keys');
      }

      // Force apply: skips hash gate, skips per-row merge (full replace)
      const result = await this.applyCloudDataToLocal(data, 'force');

      if (!result || !result.applied) {
        throw new Error(`Import refused: ${result?.reason || 'unknown'}`);
      }

      // Decouple from cloud so the next sync uploads the restored payload.
      this.updateSyncMetadata({
        lastCloudModifiedTime: null,
        lastSyncHash: null,
      });
      for (const key of SYNC_KEYS) {
        this.dirtyKeys.add(key);
      }
      this.persistSyncStateSoon();

      secureLogger.info('Backup imported successfully');

      this.notifyListeners('backup_imported', {
        success: true,
        result,
      });

      return { success: true, result };
    } catch (error) {
      secureLogger.error('Failed to import backup:', error);

      this.notifyListeners('backup_imported', {
        success: false,
        error: error.message,
      });

      return { success: false, error: error.message };
    }
  }

  // Check if service is initialized
  isInitialized() {
    return !!(this.accessToken && this.userId);
  }

  // Get sync status
  getSyncStatus() {
    const metadata = this.getSyncMetadata();
    return {
      isInitialized: this.isInitialized(),
      isOnline: this.isOnline,
      hasToken: !!this.accessToken,
      lastSyncTime: metadata?.lastSyncTime,
      syncInProgress: this.syncInProgress,
      queuedOperations: this.syncQueue.length,
      autoSyncEnabled: metadata?.autoSyncEnabled,
      deviceId: metadata?.deviceId,
      pendingChanges: this.dirtyKeys.size,
      archivedInvoiceCount: metadata?.lastArchiveCount || 0,
      archiveThresholdDays: SYNC_CONFIG.ARCHIVE_THRESHOLD_DAYS,
    };
  }

  // Toggle auto sync
  toggleAutoSync(enabled) {
    this.updateSyncMetadata({ autoSyncEnabled: enabled });
    
    if (enabled) {
      this.setupAutoSync();
      secureLogger.info('Auto sync enabled');
    } else {
      if (this.autoSyncInterval) {
        clearInterval(this.autoSyncInterval);
        this.autoSyncInterval = null;
      }
      secureLogger.info('Auto sync disabled');
    }
  }

  // Logout and clear authentication state
  logout() {
    // Clear authentication state
    this.clearAuthenticationState();

    // Stop auto sync
    if (this.autoSyncInterval) {
      clearInterval(this.autoSyncInterval);
      this.autoSyncInterval = null;
    }

    // Preserve dirtyKeys across logout so offline edits made before sign-out
    // are not abandoned: the next sign-in still uploads pending deltas.
    this.syncQueue = [];
    this.persistSyncStateSoon();

    // Clear timestamp-bound sync metadata so a wipe-and-restore on the next
    // sign-in isn't sabotaged by stale pointers. Keep deviceId and
    // autoSyncEnabled — those are user preferences, not per-session state.
    //
    // Why this matters: lastSyncTime lives in localStorage, but the data it
    // references lives in IndexedDB. If IDB gets wiped (browser "Clear site
    // data", devtools clear, cache clear) while localStorage persists, the
    // latest_wins guard in applyCloudDataToLocal would refuse the cloud
    // restore as "local_newer" because localTimestamp outlives the data.
    this.updateSyncMetadata({
      lastSyncTime: null,
      lastCloudModifiedTime: null,
      lastSyncHash: null,
      lastUploadFileId: null,
      previousBackupFileId: null,
      lastArchiveHash: null,
      lastArchiveCount: 0,
      archiveMigrated: false,
      lastBackupRotationTime: null,
    });

    // Reset sync status
    this.syncInProgress = false;

    // Notify listeners
    this.notifyListeners('logged_out', { success: true });

    secureLogger.info('Logged out from cloud sync');
  }

  // Cleanup
  destroy() {
    if (this.autoSyncInterval) {
      clearInterval(this.autoSyncInterval);
    }
    
    if (this.dataSyncTimeout) {
      clearTimeout(this.dataSyncTimeout);
    }
    
    window.removeEventListener('online', this.processQueuedSyncOperations);
    window.removeEventListener('offline', () => {});
    
    this.eventListeners.clear();
    secureLogger.info('Cloud Sync Service destroyed');
  }

  // ═══════════════════════════════════════════════════════════════
  //  COLD-STORAGE / ARCHIVE SYSTEM
  //  Paid invoices older than ARCHIVE_THRESHOLD_DAYS are moved to
  //  a separate cold_storage/ folder so the daily active backup
  //  stays small and fast.
  // ═══════════════════════════════════════════════════════════════

  // Split invoices into active (synced daily) and archive (synced rarely)
  partitionInvoiceData(allData) {
    const thresholdMs = SYNC_CONFIG.ARCHIVE_THRESHOLD_DAYS * 24 * 60 * 60 * 1000;
    const cutoffDate = Date.now() - thresholdMs;

    const invoices = allData[INVOICES_KEY] || [];
    const invoiceDetails = allData[INVOICE_DETAILS] || [];

    const activeInvoices = [];
    const archivedInvoices = [];
    const archivedIds = new Set();

    for (const inv of invoices) {
      const createdTime = new Date(inv.createdDate).getTime();
      if (inv.statusIndex === '3' && createdTime < cutoffDate) {
        archivedInvoices.push(inv);
        archivedIds.add(inv.id);
      } else {
        activeInvoices.push(inv);
      }
    }

    const activeDetails = [];
    const archivedDetails = [];

    for (const detail of invoiceDetails) {
      if (archivedIds.has(detail.id)) {
        archivedDetails.push(detail);
      } else {
        activeDetails.push(detail);
      }
    }

    // Build active data — everything except archived invoices
    const activeData = { ...allData };
    activeData[INVOICES_KEY] = activeInvoices;
    activeData[INVOICE_DETAILS] = activeDetails;
    // Embed archived IDs so other devices know not to delete them on download
    activeData._archivedInvoiceIds = Array.from(archivedIds);

    return {
      activeData,
      archiveData: {
        invoices: archivedInvoices,
        invoiceDetails: archivedDetails,
        _metadata: {
          archivedAt: Date.now(),
          thresholdDays: SYNC_CONFIG.ARCHIVE_THRESHOLD_DAYS,
          count: archivedInvoices.length,
        },
      },
      archivedIds,
    };
  }

  // Find or create cold_storage/ folder inside the user's app folder
  async setupArchiveFolder() {
    if (this.archiveFolderId) return this.archiveFolderId;

    const folder = await findOrCreateGoogleDriveFolder({
      accessToken: this.accessToken,
      folderName: SYNC_CONFIG.ARCHIVE_FOLDER_NAME,
      parentId: this.appFolderId,
    });
    this.archiveFolderId = folder.id;
    return folder.id;
  }

  // Merge newly-archived invoices with the existing archive file (dedup by ID)
  mergeArchiveData(existingArchive, newArchiveData) {
    if (!existingArchive) return newArchiveData;

    const existingInvIds = new Set((existingArchive.invoices || []).map(i => i.id));
    const existingDetIds = new Set((existingArchive.invoiceDetails || []).map(d => d.id));

    const mergedInvoices = [...(existingArchive.invoices || [])];
    const mergedDetails = [...(existingArchive.invoiceDetails || [])];

    for (const inv of newArchiveData.invoices) {
      if (!existingInvIds.has(inv.id)) {
        mergedInvoices.push(inv);
      }
    }
    for (const detail of newArchiveData.invoiceDetails) {
      if (!existingDetIds.has(detail.id)) {
        mergedDetails.push(detail);
      }
    }

    return {
      invoices: mergedInvoices,
      invoiceDetails: mergedDetails,
      _metadata: {
        ...newArchiveData._metadata,
        count: mergedInvoices.length,
        lastMergedAt: Date.now(),
      },
    };
  }

  // Upload (or merge-then-upload) archived invoices to cold_storage/
  async uploadArchiveToCloud(archiveData) {
    try {
      const folderId = await this.setupArchiveFolder();

      // Download existing archive to merge (don't overwrite — append only)
      const existing = await this.downloadArchiveFromCloud();
      const merged = this.mergeArchiveData(existing, archiveData);

      const blob = new Blob([JSON.stringify(merged, null, 2)], {
        type: 'application/json',
      });

      // Try cached fileId first
      let fileId = this.archiveFileId;
      if (fileId) {
        const updateUrl = `https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media&fields=id,modifiedTime`;
        const response = await fetch(updateUrl, {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: blob,
        });
        if (response.ok) {
          const result = await response.json();
          this.archiveFileId = result.id;
          secureLogger.info(`Archive updated: ${merged.invoices.length} invoices in cold storage`);
          return result;
        }
        if (response.status === 404) {
          this.archiveFileId = null;
          fileId = null;
        }
      }

      if (!fileId) {
        // Search for existing file
        const query = `name='${SYNC_CONFIG.ARCHIVE_FILE_NAME}' and '${folderId}' in parents and trashed=false`;
        const searchUrl = `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=files(id)`;
        const searchRes = await fetch(searchUrl, {
          headers: { Authorization: `Bearer ${this.accessToken}` },
        });
        if (searchRes.ok) {
          const searchData = await searchRes.json();
          if (searchData.files.length > 0) {
            fileId = searchData.files[0].id;
            this.archiveFileId = fileId;
            // Update existing
            const updateUrl = `https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media&fields=id,modifiedTime`;
            const updateRes = await fetch(updateUrl, {
              method: 'PATCH',
              headers: {
                Authorization: `Bearer ${this.accessToken}`,
                'Content-Type': 'application/json',
              },
              body: blob,
            });
            if (updateRes.ok) {
              const result = await updateRes.json();
              secureLogger.info(`Archive updated: ${merged.invoices.length} invoices in cold storage`);
              return result;
            }
          }
        }

        // Create new archive file
        const result = await uploadToGoogleDrive({
          accessToken: this.accessToken,
          file: blob,
          fileName: SYNC_CONFIG.ARCHIVE_FILE_NAME,
          folderId,
        });
        this.archiveFileId = result.id;
        secureLogger.info(`Archive created: ${merged.invoices.length} invoices in cold storage`);
        return result;
      }
    } catch (error) {
      secureLogger.warn('Failed to upload archive to cold storage:', error);
      throw error;
    }
  }

  // Download archived invoices from cold_storage/ (returns null if none)
  async downloadArchiveFromCloud() {
    try {
      if (!this.accessToken || !this.appFolderId) return null;
      if (!navigator.onLine) return null;

      const folderId = await this.setupArchiveFolder();

      let fileId = this.archiveFileId;
      if (!fileId) {
        const query = `name='${SYNC_CONFIG.ARCHIVE_FILE_NAME}' and '${folderId}' in parents and trashed=false`;
        const searchUrl = `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=files(id)`;
        const response = await fetch(searchUrl, {
          headers: { Authorization: `Bearer ${this.accessToken}` },
        });
        if (!response.ok) return null;
        const data = await response.json();
        if (data.files.length === 0) return null;
        fileId = data.files[0].id;
        this.archiveFileId = fileId;
      }

      const downloadUrl = `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`;
      const downloadRes = await fetch(downloadUrl, {
        headers: { Authorization: `Bearer ${this.accessToken}` },
      });
      if (!downloadRes.ok) {
        if (downloadRes.status === 404) {
          this.archiveFileId = null;
          return null;
        }
        return null;
      }

      return await downloadRes.json();
    } catch (error) {
      secureLogger.debug('Failed to download archive:', { message: error.message });
      return null;
    }
  }

  // Only upload archive when its content has actually changed
  async syncArchiveIfNeeded(archiveData) {
    if (archiveData.invoices.length === 0) return;

    const metadata = this.getSyncMetadata();
    const currentArchiveHash = await this.generateDataHash(archiveData);

    if (currentArchiveHash === metadata.lastArchiveHash) {
      return; // No new invoices to archive
    }

    await this.uploadArchiveToCloud(archiveData);
    this.updateSyncMetadata({
      lastArchiveHash: currentArchiveHash,
      lastArchiveCount: archiveData.invoices.length,
    });
  }

  // Search archived invoices by invoice number, client name, or ID
  // Downloads the archive file on demand (cached after first call)
  async searchArchive(query) {
    try {
      const archive = await this.downloadArchiveFromCloud();
      if (!archive || !archive.invoices) {
        return { invoices: [], invoiceDetails: [], totalArchived: 0 };
      }

      const q = query.toLowerCase();
      const matchingInvoices = archive.invoices.filter(inv =>
        (inv.invoiceNo && inv.invoiceNo.toLowerCase().includes(q)) ||
        (inv.clientName && inv.clientName.toLowerCase().includes(q)) ||
        (inv.id && inv.id.toLowerCase().includes(q))
      );

      const matchingIds = new Set(matchingInvoices.map(i => i.id));
      const matchingDetails = archive.invoiceDetails.filter(d => matchingIds.has(d.id));

      return {
        invoices: matchingInvoices,
        invoiceDetails: matchingDetails,
        totalArchived: archive.invoices.length,
      };
    } catch (error) {
      secureLogger.error('Archive search failed:', error);
      return { invoices: [], invoiceDetails: [], totalArchived: 0, error: error.message };
    }
  }

  // Merge archive invoices into local storage (used during full restore)
  async mergeArchiveIntoLocal(archiveData) {
    if (!archiveData || !archiveData.invoices || archiveData.invoices.length === 0) return;

    const localInvoices = await localforage.getItem(INVOICES_KEY) || [];
    const localDetails = await localforage.getItem(INVOICE_DETAILS) || [];

    const localIds = new Set(localInvoices.map(i => i.id));

    const newInvoices = archiveData.invoices.filter(i => !localIds.has(i.id));
    const newDetails = archiveData.invoiceDetails.filter(d => !localIds.has(d.id));

    if (newInvoices.length > 0) {
      this.cloudApplyDepth++;
      try {
        // Archive payloads may still be in legacy fat shape. Route writes
        // through the compacting wrapper so restored rows land deduped.
        //
        // Prepend (not append) archived rows: InvoiceTable treats the array as
        // insertion-order (oldest first) and displays `.slice(-50).reverse()`
        // as "latest". Appending the archive makes those old paid invoices
        // show up on top and pushes new invoices to the bottom. Archive
        // always contains OLDER rows than local (by cutoffDate in
        // partitionInvoiceData), so they belong at the head of the array.
        await compactingSetItem(INVOICES_KEY, [...newInvoices, ...localInvoices]);
        await compactingSetItem(INVOICE_DETAILS, [...newDetails, ...localDetails]);
      } finally {
        this.cloudApplyDepth = Math.max(0, this.cloudApplyDepth - 1);
      }
      secureLogger.info(`Restored ${newInvoices.length} archived invoices to local storage`);
    }
  }

  // Full restore: download active backup + cold-storage archive
  // Use on a new device or to recover all historical data
  async performFullRestore() {
    try {
      secureLogger.info('Starting full restore (active + archive)');

      // 1. Download and apply active backup
      const activeResult = await this.downloadFromCloud();
      if (activeResult) {
        await this.applyCloudDataToLocal(activeResult.data, 'force');
      }

      // 2. Download archive and merge into local
      const archiveData = await this.downloadArchiveFromCloud();
      if (archiveData) {
        await this.mergeArchiveIntoLocal(archiveData);
      }

      this.notifyListeners('full_restore_completed', {
        success: true,
        activeRestored: !!activeResult,
        archiveRestored: !!archiveData,
        archivedCount: archiveData?.invoices?.length || 0,
      });

      return {
        success: true,
        activeRestored: !!activeResult,
        archiveRestored: !!archiveData,
        archivedCount: archiveData?.invoices?.length || 0,
      };
    } catch (error) {
      secureLogger.error('Full restore failed:', error);
      this.notifyListeners('full_restore_completed', {
        success: false,
        error: error.message,
      });
      return { success: false, error: error.message };
    }
  }

  // Get archive status without downloading full file
  async getArchiveStatus() {
    const metadata = this.getSyncMetadata();
    return {
      lastArchiveCount: metadata.lastArchiveCount || 0,
      lastArchiveHash: metadata.lastArchiveHash || null,
      thresholdDays: SYNC_CONFIG.ARCHIVE_THRESHOLD_DAYS,
      hasColdStorage: !!(this.archiveFileId || metadata.lastArchiveCount > 0),
    };
  }

  // Helper: upload with automatic active/archive partitioning
  //
  // MIGRATION SAFETY (existing users):
  //   - First time: archive is uploaded, but the active backup still contains
  //     ALL invoices (archiveMigrated flag is false). This means if the user
  //     rolls back to old code, their backup is still complete.
  //   - Second sync onward: archiveMigrated is true, so the active backup
  //     only contains non-archived invoices (the fast path).
  //
  // UPLOAD SAFETY:
  //   - Archived invoices are only stripped from the active backup AFTER
  //     the archive file is confirmed on Drive.
  //   - If archive upload fails, the full un-partitioned data is uploaded
  //     instead, so no data is ever lost.
  async _uploadPartitioned(data, syncType) {
    const { activeData, archiveData, archivedIds } = this.partitionInvoiceData(data);
    // Share with the shrink guard so partition-driven drops in the active
    // invoice count aren't misread as suspicious.
    this._lastArchivedIds = Array.from(archivedIds || []);

    if (archiveData.invoices.length === 0) {
      // Nothing to archive — upload everything as-is
      await this.uploadToCloud(activeData, syncType);
      return;
    }

    const metadata = this.getSyncMetadata();
    const currentArchiveHash = await this.generateDataHash(archiveData);
    const archiveUnchanged = currentArchiveHash === metadata.lastArchiveHash;

    // ── Migration: first-ever partition for this user ──
    // Upload the archive but keep the active backup FULL (all invoices)
    // so a rollback to old code won't lose data.
    if (!metadata.archiveMigrated) {
      try {
        await this.uploadArchiveToCloud(archiveData);
        this.updateSyncMetadata({
          lastArchiveHash: currentArchiveHash,
          lastArchiveCount: archiveData.invoices.length,
          archiveMigrated: true,  // Next sync will use the slim active backup
        });
        secureLogger.info('Archive migration: cold-storage created, full backup preserved for this cycle');
      } catch (archiveError) {
        secureLogger.warn('Archive migration failed, continuing with full backup:', archiveError);
      }
      // Upload FULL data this time (safe for old-code rollback)
      await this.uploadToCloud(data, syncType);
      return;
    }

    // ── Steady state: archive already established ──
    if (archiveUnchanged) {
      // Archive on Drive is already up-to-date — upload active-only
      await this.uploadToCloud(activeData, syncType);
      return;
    }

    // Archive has new content — upload archive FIRST, then active
    try {
      await this.uploadArchiveToCloud(archiveData);
      this.updateSyncMetadata({
        lastArchiveHash: currentArchiveHash,
        lastArchiveCount: archiveData.invoices.length,
      });
      // Archive confirmed on Drive — safe to upload the smaller active backup
      await this.uploadToCloud(activeData, syncType);
    } catch (archiveError) {
      // Archive upload failed — fall back to uploading FULL data (no data loss)
      secureLogger.warn('Archive upload failed, uploading full backup as fallback:', archiveError);
      await this.uploadToCloud(data, syncType);
    }
  }

  // Public API for syncedStorage / slices: mark a SYNC_KEY dirty and schedule
  // a debounced auto-sync. Replaces the localforage monkey-patch so callers
  // own their writes explicitly. No-op when:
  //   - key isn't in SYNC_KEYS (forms/settings outside the sync set)
  //   - sync isn't initialized yet (boot before Drive auth)
  //   - we're inside a cloudApplyDepth scope (cloud->local apply must not
  //     bounce back as a "user change" and re-upload what we just downloaded)
  triggerSync(key, op = 'update') {
    if (!SYNC_KEYS.includes(key)) return;
    if (!this.isInitialized()) return;
    if (this.cloudApplyDepth !== 0) return;

    this.dirtyKeys.add(key);
    this.persistSyncStateSoon();
    secureLogger.info(`Data ${op} in ${key}, marked dirty`);

    if (this.dataSyncTimeout) {
      clearTimeout(this.dataSyncTimeout);
    }
    this.dataSyncTimeout = setTimeout(async () => {
      if (this.isOnline && this.getSyncMetadata().autoSyncEnabled) {
        try {
          await this.performSync('data_change');
        } catch (error) {
          secureLogger.warn('Auto-sync failed:', error);
        }
      }
    }, 3000);
  }

  // Initialize sync metadata in localStorage
  initializeSyncMetadata() {
    const existingMetadata = localStorage.getItem('cloudSyncMetadata');
    if (!existingMetadata) {
      const metadata = {
        deviceId: this.generateDeviceId(),
        autoSyncEnabled: true,
        lastSyncTime: null,
        lastCloudModifiedTime: null,
        lastUploadFileId: null,
        lastArchiveHash: null,
        lastArchiveCount: 0,
        previousBackupFileId: null,
        lastBackupRotationTime: null,
        syncVersion: 1,
        initialized: true
      };
      localStorage.setItem('cloudSyncMetadata', JSON.stringify(metadata));
    }
  }

  // Get sync metadata from localStorage
  getSyncMetadata() {
    try {
      const metadata = localStorage.getItem('cloudSyncMetadata');
      if (metadata) {
        return JSON.parse(metadata);
      }
    } catch (error) {
      secureLogger.error('Failed to get sync metadata:', error);
    }
    
    // Return default metadata if none exists
    return {
      deviceId: this.generateDeviceId(),
      autoSyncEnabled: true,
      lastSyncTime: null,
      lastCloudModifiedTime: null,
      lastUploadFileId: null,
      lastArchiveHash: null,
      lastArchiveCount: 0,
      previousBackupFileId: null,
      lastBackupRotationTime: null,
      syncVersion: 1,
      initialized: true
    };
  }

  // Update sync metadata in localStorage
  updateSyncMetadata(updates) {
    try {
      const currentMetadata = this.getSyncMetadata();
      const newMetadata = { ...currentMetadata, ...updates };
      localStorage.setItem('cloudSyncMetadata', JSON.stringify(newMetadata));
    } catch (error) {
      secureLogger.error('Failed to update sync metadata:', error);
    }
  }

  // Generate unique device ID
  generateDeviceId() {
    return `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Setup network listeners for online/offline detection
  setupNetworkListeners() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      secureLogger.info('Network connection restored');
      this.processQueuedSyncOperations();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      secureLogger.info('Network connection lost');
    });
  }

  // Setup automatic sync interval based on subscription plan
  setupAutoSync() {
    if (this.autoSyncInterval) {
      clearInterval(this.autoSyncInterval);
    }

    // Get auto-sync frequency from subscription plan
    const getAutoSyncFrequency = () => {
      try {
        // Dynamic import to avoid circular dependency
        return import('../services/subscriptionService').then(module => {
          const subscriptionService = module.default;
          return subscriptionService.getAutoSyncFrequency();
        });
      } catch (error) {
        secureLogger.warn('Could not get auto-sync frequency, defaulting to manual:', error);
        return Promise.resolve(0); // Manual only
      }
    };

    // Setup auto-sync based on plan limitations
    getAutoSyncFrequency().then(frequencyMinutes => {
      if (frequencyMinutes > 0) {
        secureLogger.info(`Setting up auto-sync every ${frequencyMinutes} minutes`);
        this.autoSyncInterval = setInterval(async () => {
          if (this.isOnline && this.getSyncMetadata().autoSyncEnabled && this.isInitialized()) {
            try {
              await this.performSync('auto');
            } catch (error) {
              secureLogger.warn('Auto-sync failed:', error);
            }
          }
        }, frequencyMinutes * 60 * 1000);
      } else {
        secureLogger.info('Auto-sync disabled by subscription plan (manual only)');
      }
    }).catch(error => {
      secureLogger.warn('Failed to setup auto-sync:', error);
    });
  }

  // Clear authentication state
  //
  // We do NOT clear dirtyKeys here. The same user signing back in must be
  // able to resume sync with the deltas they made offline. User-switch is
  // the only path that must wipe dirty state, and it runs its own wipe in
  // _handleUserSwitch.
  clearAuthenticationState() {
    this.accessToken = null;
    this.userId = null;
    this.appFolderId = null;
    this.backupFileId = null;
    this.previousBackupFileId = null;
    this.archiveFolderId = null;
    this.archiveFileId = null;
    this.versionsFolderId = null;

    try {
      clearGoogleAuth();
    } catch (error) {
      secureLogger.error('Failed to clear authentication state:', error);
    }
  }

  // Detect Google-account switch on the same device. If a different userId
  // signs in than the one previously stored, wipe local SYNC_KEYS + sync
  // state so we don't:
  //   1. Upload user A's IndexedDB data into user B's Drive folder
  //   2. Show user A's clients/invoices to user B before initial sync runs
  //
  // Plain logout (no new sign-in) does NOT clear IDB — preserves offline
  // data for accidental sign-outs and lets users keep working pre-auth.
  // Wraps the clears in cloudApplyDepth so the install-time hook ignores
  // them (otherwise the clears would mark every key dirty and trigger a
  // sync uploading empty arrays).
  async _handleUserSwitch(userId) {
    if (!userId) return;
    let lastUserId = null;
    try {
      lastUserId = localStorage.getItem(LAST_USER_KEY);
    } catch (error) {
      // localStorage can throw in private mode / blocked origins — treat
      // as "no prior user" rather than blocking sign-in.
      secureLogger.warn('Failed to read last user id:', error);
    }

    if (lastUserId && lastUserId !== userId) {
      secureLogger.info('User switch detected — clearing local sync caches before initial sync');
      this.cloudApplyDepth++;
      try {
        for (const key of SYNC_KEYS) {
          try {
            await localforage.removeItem(key);
          } catch (error) {
            secureLogger.warn(`Failed to clear ${key} on user switch:`, error);
          }
        }
        // Drop the shared _assets map too — it's tenant-specific (user A's
        // logo bytes are not user B's) and the in-memory cache must reload
        // so the next read doesn't hand out stale images.
        try {
          await localforage.removeItem(ASSETS_KEY);
          invalidateAssetsCache();
        } catch (error) {
          secureLogger.warn('Failed to clear _assets on user switch:', error);
        }
        try {
          await localforage.removeItem(SYNC_STATE_KEY);
        } catch (error) {
          secureLogger.warn('Failed to clear sync state on user switch:', error);
        }
        // Reset in-memory tracking so post-switch sync starts clean
        this.dirtyKeys.clear();
        this.syncQueue = [];
        this.lastSyncTime = null;
        this.backupFileId = null;
        this.previousBackupFileId = null;
        this.archiveFolderId = null;
        this.archiveFileId = null;
        this.versionsFolderId = null;
        // Drop sync metadata that pinned us to the previous user's Drive files
        this.updateSyncMetadata({
          lastSyncTime: null,
          lastCloudModifiedTime: null,
          lastUploadFileId: null,
          lastSyncHash: null,
          lastArchiveHash: null,
          lastArchiveCount: 0,
          previousBackupFileId: null,
          lastBackupRotationTime: null,
        });
      } finally {
        this.cloudApplyDepth = Math.max(0, this.cloudApplyDepth - 1);
      }
      this.notifyListeners('user_switched', { previousUserId: lastUserId, newUserId: userId });
    }

    try {
      localStorage.setItem(LAST_USER_KEY, userId);
    } catch (error) {
      secureLogger.warn('Failed to record last user id:', error);
    }
  }

  // ═══════════════════════════════════════════════════════════════
  //  SYNC-STATE PERSISTENCE
  //  dirtyKeys + syncQueue live in memory and would vanish on tab
  //  close mid-debounce. Persist them to localforage under a dedicated
  //  key so a reload picks up where we left off.
  // ═══════════════════════════════════════════════════════════════

  // Read the persisted state once on startup. Restored entries become
  // pending writes for the next sync.
  async restoreSyncState() {
    try {
      const state = await localforage.getItem(SYNC_STATE_KEY);
      if (!state || typeof state !== 'object') return;
      if (Array.isArray(state.dirtyKeys)) {
        for (const key of state.dirtyKeys) {
          if (SYNC_KEYS.includes(key)) this.dirtyKeys.add(key);
        }
      }
      if (Array.isArray(state.syncQueue)) {
        this.syncQueue.push(...state.syncQueue);
      }
      if (this.dirtyKeys.size > 0 || this.syncQueue.length > 0) {
        secureLogger.info(
          `Restored sync state: ${this.dirtyKeys.size} dirty key(s), ${this.syncQueue.length} queued op(s)`
        );
      }
    } catch (error) {
      secureLogger.warn('Failed to restore sync state:', error);
    }
  }

  // Debounced write — coalesces bursts of dirty-key mutations into one
  // localforage write. Writes via the original (un-hooked) localforage
  // path so it doesn't recursively mark itself dirty.
  persistSyncStateSoon() {
    if (this._persistStateTimer) clearTimeout(this._persistStateTimer);
    this._persistStateTimer = setTimeout(() => {
      this._persistStateTimer = null;
      const payload = {
        dirtyKeys: Array.from(this.dirtyKeys),
        syncQueue: this.syncQueue.slice(0, 32), // cap to avoid runaway
        savedAt: Date.now(),
      };
      // SYNC_STATE_KEY is not in SYNC_KEYS, so the hook will skip it,
      // but we still go through the patched setItem for consistency.
      localforage.setItem(SYNC_STATE_KEY, payload).catch(error => {
        secureLogger.warn('Failed to persist sync state:', error);
      });
    }, 500);
  }

  // ═══════════════════════════════════════════════════════════════
  //  BACKUP ROTATION
  //  Once per BACKUP_ROTATION_INTERVAL_MS, snapshot the current backup
  //  to a "previous" file before overwriting. Gives one-click recovery
  //  if a sync uploads bad data (e.g. an app bug producing empty arrays).
  // ═══════════════════════════════════════════════════════════════

  async rotateBackupIfDue() {
    if (!this.accessToken || !this.appFolderId) return;
    const meta = this.getSyncMetadata();
    const lastRot = meta.lastBackupRotationTime || 0;
    const dueAt = lastRot + SYNC_CONFIG.BACKUP_ROTATION_INTERVAL_MS;
    if (Date.now() < dueAt) return;

    // Need a current backup to rotate. If none exists, this is the first
    // sync — nothing to snapshot yet, just stamp the timestamp.
    const currentId = this.backupFileId || meta.lastUploadFileId;
    if (!currentId) {
      this.updateSyncMetadata({ lastBackupRotationTime: Date.now() });
      return;
    }

    try {
      const snap = await this.createVersionedSnapshot({ manual: false });
      if (snap) {
        // Best-effort: prune old automated snapshots beyond the retention window.
        this.pruneOldVersions().catch(e =>
          secureLogger.warn('Version prune failed:', e?.message || e)
        );
        this.updateSyncMetadata({
          lastBackupRotationTime: Date.now(),
          // Point the legacy "previous backup" pointer at the newest auto
          // snapshot so existing restorePreviousBackup callers still work.
          previousBackupFileId: snap.id,
        });
        this.previousBackupFileId = snap.id;
        secureLogger.info(`Versioned snapshot created: ${snap.name}`);
      }
    } catch (error) {
      secureLogger.warn('Versioned snapshot failed:', error);
    }
  }

  // ═══════════════════════════════════════════════════════════════
  //  VERSIONED SNAPSHOTS  (AWS RDS–style retention)
  //  Each day a server-side copy of the active backup lands in the
  //  `versions/` subfolder with a timestamped name. Automated snaps
  //  are pruned beyond VERSION_AUTO_RETENTION. Manual snaps are kept
  //  forever (distinct filename prefix, distinct description tag).
  // ═══════════════════════════════════════════════════════════════

  async setupVersionsFolder() {
    if (this.versionsFolderId) return this.versionsFolderId;
    const folder = await findOrCreateGoogleDriveFolder({
      accessToken: this.accessToken,
      folderName: SYNC_CONFIG.VERSIONS_FOLDER_NAME,
      parentId: this.appFolderId,
    });
    this.versionsFolderId = folder.id;
    return folder.id;
  }

  // Timestamp safe for Drive filenames (no colons / dots).
  _snapshotTimestamp() {
    return new Date().toISOString().replace(/[:.]/g, '-');
  }

  // Server-side copy of the active backup into versions/. Manual snapshots
  // use a different prefix + description tag so prune skips them.
  async createVersionedSnapshot({ manual = false } = {}) {
    if (!this.accessToken || !this.appFolderId) return null;
    const currentId = this.backupFileId || this.getSyncMetadata().lastUploadFileId;
    if (!currentId) {
      secureLogger.debug('No active backup to snapshot yet');
      return null;
    }

    const folderId = await this.setupVersionsFolder();
    const prefix = manual ? SYNC_CONFIG.VERSION_MANUAL_PREFIX : SYNC_CONFIG.VERSION_AUTO_PREFIX;
    const name = `${prefix}${this._snapshotTimestamp()}.json`;

    const copyUrl = `https://www.googleapis.com/drive/v3/files/${currentId}/copy?fields=id,name,createdTime,size`;
    const res = await fetch(copyUrl, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name,
        parents: [folderId],
        description: manual ? 'manual' : 'auto',
      }),
    });

    if (!res.ok) {
      secureLogger.warn(`Versioned snapshot copy failed: ${res.status}`);
      return null;
    }
    return await res.json();
  }

  // List snapshots in versions/. Sorted newest-first. Manual detection uses
  // the filename prefix (robust across Drive description edits).
  async listBackupVersions() {
    if (!this.accessToken || !this.appFolderId) return [];
    const folderId = await this.setupVersionsFolder();
    const q = `'${folderId}' in parents and trashed=false`;
    const url =
      `https://www.googleapis.com/drive/v3/files?` +
      `q=${encodeURIComponent(q)}` +
      `&fields=files(id,name,createdTime,modifiedTime,size,description)` +
      `&orderBy=createdTime desc` +
      `&pageSize=100`;
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${this.accessToken}` },
    });
    if (!res.ok) return [];
    const json = await res.json();
    return (json.files || []).map(f => ({
      id: f.id,
      name: f.name,
      createdTime: f.createdTime,
      modifiedTime: f.modifiedTime,
      size: Number(f.size) || 0,
      manual:
        (f.name && f.name.startsWith(SYNC_CONFIG.VERSION_MANUAL_PREFIX)) ||
        f.description === 'manual',
    }));
  }

  // Preview a snapshot before restoring. Downloads the blob but does NOT
  // touch local storage — returns row counts by entity + the snapshot's
  // own metadata. Used by the UI diff preview ("Will replace 52 invoices
  // → 47 invoices") so a user never blind-confirms a destructive restore.
  async peekBackupVersion(versionId) {
    if (!this.accessToken || !versionId) {
      throw new Error('Not authenticated or missing versionId');
    }
    const dlUrl = `https://www.googleapis.com/drive/v3/files/${versionId}?alt=media`;
    const dlRes = await fetch(dlUrl, {
      headers: { Authorization: `Bearer ${this.accessToken}` },
    });
    if (!dlRes.ok) {
      throw new Error(`Failed to download version: ${dlRes.status}`);
    }
    const data = await dlRes.json();
    return {
      counts: this._countRowsByKey(data),
      modifiedTime: data?._metadata?.timestamp || null,
      schemaVersion: data?._metadata?.schemaVersion ?? 1,
      hashVersion: data?._metadata?.hashVersion ?? 1,
      deviceId: data?._metadata?.deviceId || null,
    };
  }

  // Read row counts from local IDB without hydrating through Redux. Pairs
  // with peekBackupVersion to build the pre-restore diff.
  async getLocalCounts() {
    const keys = [INVOICES_KEY, INVOICE_DETAILS, CLIENTS_KEY, PRODUCTS_KEY, QUOTATIONS_KEY, QUOTATION_DETAILS];
    const counts = {};
    for (const key of keys) {
      try {
        const v = await localforage.getItem(key);
        counts[key] = Array.isArray(v) ? v.length : 0;
      } catch (_) {
        counts[key] = 0;
      }
    }
    return counts;
  }

  // Download a specific version and apply it via force (bypasses hash + merge).
  // Mirrors the importBackup "decouple from cloud" step so the next sync
  // pushes the restored payload instead of pulling the old Drive copy back.
  //
  // Safety snapshot: before applying, server-side-copy the CURRENT active
  // backup to versions/ as a manual snapshot. If the restore turns out to
  // be the wrong version (or the chosen snapshot itself is damaged), the
  // user can revert again to that pre-revert marker instead of losing
  // current state forever. Disable via `{ safetySnapshot: false }` only
  // when the current state is already known-bad.
  async restoreBackupVersion(versionId, { safetySnapshot = true } = {}) {
    try {
      if (!this.accessToken || !versionId) {
        throw new Error('Not authenticated or missing versionId');
      }

      // Take the safety snapshot first. Best-effort: if it fails we log and
      // proceed — refusing the restore because the safety net couldn't be
      // set up is worse than a slightly riskier restore, since the user
      // explicitly asked for this action.
      let safetySnapshotInfo = null;
      if (safetySnapshot) {
        try {
          safetySnapshotInfo = await this.createVersionedSnapshot({ manual: true });
          if (safetySnapshotInfo) {
            secureLogger.info(`Pre-revert safety snapshot created: ${safetySnapshotInfo.name}`);
          }
        } catch (snapErr) {
          secureLogger.warn('Pre-revert safety snapshot failed (continuing with restore):', snapErr);
        }
      }

      const dlUrl = `https://www.googleapis.com/drive/v3/files/${versionId}?alt=media`;
      const dlRes = await fetch(dlUrl, {
        headers: { Authorization: `Bearer ${this.accessToken}` },
      });
      if (!dlRes.ok) {
        throw new Error(`Failed to download version: ${dlRes.status}`);
      }
      const data = await dlRes.json();
      const result = await this.applyCloudDataToLocal(data, 'force');
      if (!result || !result.applied) {
        throw new Error(`Restore refused: ${result?.reason || 'unknown'}`);
      }

      this.updateSyncMetadata({
        lastCloudModifiedTime: null,
        lastSyncHash: null,
      });
      for (const key of SYNC_KEYS) this.dirtyKeys.add(key);
      this.persistSyncStateSoon();

      this.notifyListeners('version_restored', {
        success: true,
        versionId,
        safetySnapshot: safetySnapshotInfo,
        result,
      });
      return { success: true, result, safetySnapshot: safetySnapshotInfo };
    } catch (error) {
      secureLogger.error('Failed to restore version:', error);
      this.notifyListeners('version_restored', { success: false, error: error.message });
      return { success: false, error: error.message };
    }
  }

  // Prune automated snapshots beyond VERSION_AUTO_RETENTION. Manual snapshots
  // are never touched. Deletes are best-effort (logged, never thrown).
  async pruneOldVersions(retainCount = SYNC_CONFIG.VERSION_AUTO_RETENTION) {
    const all = await this.listBackupVersions();
    const auto = all.filter(v => !v.manual);
    if (auto.length <= retainCount) return { deleted: 0 };

    const toDelete = auto.slice(retainCount); // list is sorted desc; tail is oldest
    let deleted = 0;
    for (const v of toDelete) {
      try {
        const res = await fetch(`https://www.googleapis.com/drive/v3/files/${v.id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${this.accessToken}` },
        });
        if (res.ok || res.status === 204) deleted++;
      } catch (e) {
        secureLogger.warn('Prune delete failed:', e?.message || e);
      }
    }
    secureLogger.info(`Pruned ${deleted} old automated snapshots`);
    return { deleted };
  }

  // Download the previous-generation backup. Returns parsed JSON or null.
  // Wire this to a UI button (e.g. Settings → "Restore previous backup")
  // for one-click rollback after a bad sync.
  //
  // Prefers the newest automated snapshot in versions/ (AWS RDS–style).
  // Falls back to the legacy single-slot file so upgraded users with no
  // versioned snapshots yet can still roll back their last sync.
  async restorePreviousBackup() {
    try {
      if (!this.accessToken || !this.appFolderId) return null;

      // Prefer newest auto snapshot in versions/
      const versions = await this.listBackupVersions();
      const newestAuto = versions.find(v => !v.manual);
      if (newestAuto) {
        const result = await this.restoreBackupVersion(newestAuto.id);
        this.notifyListeners('previous_backup_restored', {
          success: result.success,
          result: result.result,
          error: result.error,
        });
        return result;
      }

      // Legacy fallback — single logislip_backup.previous.json slot
      const meta = this.getSyncMetadata();
      let prevId = this.previousBackupFileId || meta.previousBackupFileId;
      if (!prevId) {
        const q = `name='${SYNC_CONFIG.BACKUP_PREVIOUS_FILE_NAME}' and '${this.appFolderId}' in parents and trashed=false`;
        const url = `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(q)}&fields=files(id)&pageSize=1`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${this.accessToken}` } });
        if (!res.ok) return null;
        const json = await res.json();
        if (!json.files || json.files.length === 0) return null;
        prevId = json.files[0].id;
        this.previousBackupFileId = prevId;
      }
      const dlUrl = `https://www.googleapis.com/drive/v3/files/${prevId}?alt=media`;
      const dlRes = await fetch(dlUrl, { headers: { Authorization: `Bearer ${this.accessToken}` } });
      if (!dlRes.ok) return null;
      const data = await dlRes.json();
      const result = await this.applyCloudDataToLocal(data, 'force');
      if (result && result.applied) {
        this.updateSyncMetadata({ lastCloudModifiedTime: null, lastSyncHash: null });
        for (const key of SYNC_KEYS) this.dirtyKeys.add(key);
        this.persistSyncStateSoon();
      }
      this.notifyListeners('previous_backup_restored', { success: true, result });
      return result;
    } catch (error) {
      secureLogger.error('Failed to restore previous backup:', error);
      this.notifyListeners('previous_backup_restored', { success: false, error: error.message });
      return null;
    }
  }
}

// Export singleton instance
export const cloudSyncService = new CloudSyncService();
export default cloudSyncService;
